import React, { useEffect, useState } from "react";
import classNames from "classnames";
import Portal from "elements/Portal";
import { WEBSITE_BUILDER_Z_LAYERS } from "../../utility/websiteBuilderZLayers";
import { useWebsiteBuilderEditorContext } from "../../websiteBuilderEditorContext";
import { TWebsiteBuilderRichTextType } from "../websiteBuilderRichTextContent";
import { WebsiteBuilderHeadingRichTextMenu } from "./WebsiteBuilderHeadingRichTextMenu";
import { WebsiteBuilderParagraphRichTextMenu } from "./WebsiteBuilderParagraphRichTextMenu";
import "./websiteBuilderRichTextMenu.scss";

export type TWebsiteBuilderRichTextMenuPlacement = "top" | "bottom";

interface IWebsiteBuilderRichTextMenuProps {
  elementId: string;
  type: TWebsiteBuilderRichTextType;
  elementBuilderUIPortalId: string;
  sectionId: string;
}

const RICH_TEXT_MENU_HEIGHT = 40;
const RICH_TEXT_MENU_GAP = 8;
const RICH_TEXT_MENU_TOTAL_HEIGHT = RICH_TEXT_MENU_HEIGHT + RICH_TEXT_MENU_GAP;

export function WebsiteBuilderRichTextMenu(props: IWebsiteBuilderRichTextMenuProps) {
  const { elementId, type, elementBuilderUIPortalId, sectionId } = props;

  const { activePageDomNodeRefMap } = useWebsiteBuilderEditorContext();

  const [bottomOffset, setBottomOffset] = useState<number>(null);

  const menuPlacement: TWebsiteBuilderRichTextMenuPlacement = bottomOffset != null ? "bottom" : "top";

  useEffect(() => {
    const anchor = document.getElementById(elementBuilderUIPortalId);
    const section = activePageDomNodeRefMap.current.get(sectionId);
    const element = activePageDomNodeRefMap.current.get(elementId);

    if (anchor == null || section == null || element == null) {
      setBottomOffset(null);
      return;
    }

    const anchorTop = anchor.getBoundingClientRect().top;
    const availableSpace = anchorTop - section.getBoundingClientRect().top;

    setBottomOffset(
      availableSpace < RICH_TEXT_MENU_TOTAL_HEIGHT
        ? element.getBoundingClientRect().bottom - anchorTop
        : null,
    );
  }, [elementBuilderUIPortalId, sectionId, elementId]);

  return (
    <Portal parentId={elementBuilderUIPortalId}>
      <div
        className={classNames("website-builder-rich-text-menu", { "rich-text-menu-bottom": menuPlacement === "bottom" })}
        style={{
          top: bottomOffset ?? 0,
          height: RICH_TEXT_MENU_HEIGHT,
          marginTop: menuPlacement === "bottom" ? RICH_TEXT_MENU_GAP : -RICH_TEXT_MENU_GAP,
          zIndex: WEBSITE_BUILDER_Z_LAYERS.designSpace.elementBuilderUI,
        }}
        onPointerDown={event => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onDoubleClick={event => event.stopPropagation()}
      >
        {type === "heading" ? (
          <WebsiteBuilderHeadingRichTextMenu elementId={elementId} menuPlacement={menuPlacement} />
        ) : (
          <WebsiteBuilderParagraphRichTextMenu elementId={elementId} menuPlacement={menuPlacement} />
        )}
      </div>
    </Portal>
  );
}
